import React, { useEffect, useRef } from 'react';

/**
 * BQ-19 (absorbed BQ-7 ConfirmModal infra): shared confirm modal used by
 * batch label operations and the filter-aware export scope chooser. Future
 * BQ-7 work will reuse this for file-upload-overwrite + Clear-workspace.
 *
 * Behavior contract:
 *  - Returns null when !isOpen so it can be mounted unconditionally.
 *  - Backdrop click cancels.
 *  - ESC cancels, Enter confirms (when modal is open and user is not typing
 *    into a multi-line field — a single-line confirm shouldn't intercept
 *    keystrokes elsewhere on the page because we register the listener only
 *    while open).
 *  - `destructive` swaps the confirm button to red so callers don't have to
 *    pass arbitrary classNames.
 */
export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}) => {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Focus the confirm button on open so Enter naturally confirms.
  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => confirmButtonRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen]);

  // Global ESC / Enter handlers — only attached while open.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        onConfirm();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onCancel, onConfirm]);

  if (!isOpen) return null;

  const confirmClass = destructive
    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
      style={{ zIndex: 10001 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-2xl border border-gray-200 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200">
          <h3
            id="confirm-modal-title"
            className="text-base font-semibold text-gray-900"
          >
            {title}
          </h3>
        </div>
        <div className="px-5 py-4 text-sm text-gray-700">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-2 rounded-b-lg">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            className={`px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors focus:outline-none focus:ring-2 ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
